import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { FDI_Notation } from '@/common/FDI_Notation';
import type { ToothConstraintValues } from '@correction/config/treatmentConstraints';

type ConstraintGroup = 'translationLimit' | 'rotationLimitDeg';
type ConstraintAxis = 'x' | 'y' | 'z';

type ConstraintRow = {
  tooth: FDI_Notation;
  constraint: ToothConstraintValues;
};

type TreatmentConstraintsTableProps = {
  rows: ConstraintRow[];
  onChange: (tooth: FDI_Notation, group: ConstraintGroup, axis: ConstraintAxis, value: number) => void;
  onReset: () => void;
  onClose: () => void;
  className?: string;
};

export function TreatmentConstraintsTable({ rows, onChange, onReset, onClose, className }: TreatmentConstraintsTableProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  if (!mounted || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div className={cn('fixed bottom-6 right-6 z-[1200] pointer-events-auto', className)}>
      <section className="w-full max-w-[800px] rounded-2xl border border-white/8 bg-content/95 p-4 text-white shadow-2xl shadow-black/50 backdrop-blur">
        <header className="flex items-center justify-between">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/45">Treatment constraints</p>
            <h2 className="text-base font-semibold text-white">Movement limits</h2>
          </div>
          <div className="flex items-center gap-1.5">
            <Button variant="ghost-light" size="sm" type="button" onClick={onReset}>
              Reset limits
            </Button>
            <Button variant="ghost-light" size="sm" type="button" onClick={onClose}>
              Close
            </Button>
          </div>
        </header>

        <p className="mt-2 text-xs text-white/60">
          Adjust the maximum movement allowed per tooth. Changes apply instantly to both jaws.
        </p>

        <div className="mt-4 max-h-60 overflow-y-auto rounded-xl border border-white/8 bg-content-dark/60">
          <table className="min-w-full table-fixed text-xs">
            <thead className="sticky top-0 bg-content-dark/90 text-[10px] uppercase tracking-[0.16em] text-white/55">
              <tr>
                <th className="px-3 py-3 text-left">Tooth</th>
                <th className="px-3 py-3 text-left">Left / Right (mm)</th>
                <th className="px-3 py-3 text-left">Extrusion / Intrusion (mm)</th>
                <th className="px-3 py-3 text-left">Forward / Backward (mm)</th>
                <th className="px-3 py-3 text-left">Inclination (°)</th>
                <th className="px-3 py-3 text-left">Angulation (°)</th>
                <th className="px-3 py-3 text-left">Rotation (°)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ tooth, constraint }) => (
                <tr key={tooth} className="odd:bg-content/15">
                  <td className="px-3 py-2 font-semibold text-white/80">{tooth}</td>
                  {(['x', 'y', 'z'] as ConstraintAxis[]).map((axis) => (
                    <td key={`t-${tooth}-${axis}`} className="px-3 py-2">
                      <Input
                        aria-label={`${tooth} translation ${axis}`}
                        type="number"
                        className="h-8 w-20 bg-content/40 text-xs"
                        value={constraint.translationLimit[axis]}
                        step="0.05"
                        min="0"
                        onChange={(event) => {
                          const parsed = Number.parseFloat(event.target.value);
                          if (Number.isNaN(parsed)) return;
                          onChange(tooth, 'translationLimit', axis, parsed);
                        }}
                      />
                    </td>
                  ))}
                  {(['x', 'y', 'z'] as ConstraintAxis[]).map((axis) => (
                    <td key={`r-${tooth}-${axis}`} className="px-3 py-2">
                      <Input
                        aria-label={`${tooth} rotation ${axis}`}
                        type="number"
                        className="h-8 w-full bg-content/40 text-xs"
                        value={constraint.rotationLimitDeg[axis]}
                        step="0.1"
                        min="0"
                        onChange={(event) => {
                          const parsed = Number.parseFloat(event.target.value);
                          if (Number.isNaN(parsed)) return;
                          onChange(tooth, 'rotationLimitDeg', axis, parsed);
                        }}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>,
    document.body,
  );
}
